import FieldResponse from '../model/FieldResponse';
import m from './module';
import { createError } from '../error';

class FieldResponseService {
  constructor($q, FieldInstanceService, LogService, DateTimeService) {
    this.$q = $q;
    this.FieldInstanceService = FieldInstanceService;
    this.LogService = LogService;
    this.DateTimeService = DateTimeService;
  }

  create(data) {
    const promises = [];
    data = data || {};
    const fieldResponseId = data.id || null;
    const fieldInstance = data.fieldInstance || null;
    const fieldInstanceId = fieldInstance && fieldInstance.id || data.events_custom_instances_id || null;
    const recapDefinitionId = data.recapDefinitionId || data.events_recaps_id || null;
    const eventTimezoneName = data.eventTimezoneName || null;

    if (!fieldInstance) {
      if (!fieldInstanceId) {
        this.LogService.warn(`FieldResponseService.create() did not get FieldInstance for FieldResponse ${fieldResponseId} on recap definition ${recapDefinitionId}`);
        return this.$q.reject(createError({
          name: "NoFieldInstanceProvided",
          message: "Field instance required for FieldResponse",
        }));
      }
      promises.push(
        this.$q
          .when(this.FieldInstanceService.getById(fieldInstanceId))
          .then(fieldInstance => {
            if (!fieldInstance) {
              this.LogService.warn(`FieldResponseService.create() unable to retrieve field instance ${fieldInstanceId} for FieldResponse ${fieldResponseId} on recap definition ${recapDefinitionId}`);
              return this.$q.reject(createError({
                error: 'InvalidFieldInstance',
                message: `Unable to retrieve field instance ${fieldInstanceId} for field response ${fieldResponseId} on recap definition ${recapDefinitionId}`,
              }));
            }
            const fieldId = fieldInstance && fieldInstance.field && fieldInstance.field.id;
            data.fieldInstance = fieldInstance;
            data.events_custom_instances_id = fieldInstance.id;
            data.events_custom_field_blocks_id = fieldInstance.events_custom_field_blocks_id;

            const fieldType = fieldInstance && fieldInstance.field && fieldInstance.field.fieldType;
            if (data.value && fieldType) {
              // Convert value from API, if necessary
              if (fieldType.isDateTime ) {
                const date = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(data.value, eventTimezoneName);
                this.LogService.debug(`FieldResponseService.create() converted datetime '${data.value}' to '${date}'. eventTimezoneName: ${eventTimezoneName} field id: ${fieldId}`);
                data.value = date;
              } else if (fieldType.isDate) {
                // For dates, always use local timezone. No conversion.
                const date = this.DateTimeService.parseDateWithLocalTimezone(data.value);
                this.LogService.debug(`FieldResponseService.create() converted date '${data.value}' to '${date}'. eventTimezoneName: ${eventTimezoneName} field id: ${fieldId}`);
                data.value = date;
              } else if (fieldType.isTime) {
                const date = this.DateTimeService.parseTimeWithTimezoneOrServerTimezone(data.value, null, eventTimezoneName);
                this.LogService.debug(`FieldResponseService.create() converted time '${data.value}' to '${date}'. eventTimezoneName: ${eventTimezoneName} field id: ${fieldId}`);
                data.value = date;
              } else {
                // this.LogService.debug(`Don't need to convert date for field instance ${fieldInstance.id}; type is ${fieldType}`);
                this.LogService.debug(`FieldResponseService.create() not DateTime, Date, or Time for field ${fieldId}`)
              }
            } else {
              // this.LogService.debug(`Not converting becuase dont' have both fieldType (${fieldType && fieldType.defined_as}) and data.value (${data.value})`);
              this.LogService.debug(`FieldResponseService.create() missing value or fieldType for field ${fieldId}`);
            }
          })
      )
    }
    return this.$q
      .all(promises)
      .then(() => {
        return new FieldResponse(data);
      });
  }

  serialize(fieldResponse) {
    let value = fieldResponse.value;
    const field = fieldResponse.fieldInstance && fieldResponse.fieldInstance.field;
    const fieldType = field && field.fieldType;
    const fieldId = field && field.id;

    // Type conversion for API submission, if necessary
    if (fieldType && fieldType.isDateTime) {
      value = this.DateTimeService.serializeDateTimeForAPI(value, fieldResponse.eventTimezoneName);
      this.LogService.debug(`FieldResponseService.serialize() converted datetime '${fieldResponse.value}' to '${value}'. eventTimezoneName: ${fieldResponse.eventTimezoneName} field id: ${fieldId}`);
    } else if (fieldType && fieldType.isDate) {
      // Don't pass event timezone for date field
      value = this.DateTimeService.serializeDateForAPI(value);
      this.LogService.debug(`FieldResponseService.serialize() converted date '${fieldResponse.value}' to '${value}'. field id: ${fieldId}`);
    } else if (fieldType && fieldType.isTime) {
      this.LogService.debug(`FieldResponseService.serialize() converted time '${fieldResponse.value}' to '${value}'. eventTimezoneName: ${fieldResponse.eventTimezoneName} field id: ${fieldId}`);
      value = this.DateTimeService.serializeTimeForAPI(value, fieldResponse.eventTimezoneName);
    }

    return {
      id: fieldResponse.id,
      value: value,
      visible_on_submit: fieldResponse.visible,
      data_link_field_names: (field && field.data_link_field_names) || null,
      events_id: fieldResponse.events_id,
      events_custom_id: fieldResponse.events_custom_id,
      events_custom_responses_id: fieldResponse.events_custom_responses_id,
      events_custom_forms_responses_id: fieldResponse.events_custom_forms_responses_id,
      events_custom_field_blocks_id: fieldResponse.fieldInstance?.events_custom_field_blocks_id,
      events_custom_instances_id: fieldResponse.events_custom_instances_id,
      document_uuids: fieldResponse.document_uuids || [],
    };
  }
}

m.service('FieldResponseService', FieldResponseService);

export default FieldResponseService;
